/**
 * Tümdengelim ızgarası ve terazi.
 *
 * İkisinin de ortak yanı kurgunun GERİYE DOĞRU üretilmesi: önce doğru
 * çözüm, sonra o çözümden türetilmiş ipuçları. İleri doğru üretip
 * "acaba çözülüyor mu" diye bakmak, bu projede iki kez denendi ve iki
 * kez çöktü (`arith_grid`, `rule_maze`). Kurgu bulunamadığında sayfa
 * üretilemiyor ve bir çocuk ödevsiz kalıyor.
 *
 * TEKİLLİK VARSAYILMIYOR, SAYILIYOR. Çözümden ipucu türetmek,
 * ipuçlarının tutarlı olmasını garanti eder ama çözümün TEK olmasını
 * etmez: eksik ipuçlu bir ızgaranın birden çok geçerli çözümü olur ve
 * çocuğun verdiği geçerli cevap yanlış sayılır. Burada ipuçları,
 * olasılıkların tamamı sayılarak tekillik görülene kadar ekleniyor.
 */

import { Rng } from '../../prng'
import { ayrilma, basHarf, foldTr, gradeList, tamlayan } from '../helpers'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { ExerciseTemplate, GradeResult, ItemResult, RenderNode } from '../types'
import type { ShapeName } from '../../render/shapes'

/* ================================================================== *
 * 43. Tümdengelim ızgarası
 *
 * Klasik "Einstein bulmacası" kurgusu: sabit bir sıra (1., 2., 3.) ve
 * o sıraya dağılmış iki ya da üç özellik. İpuçları hem olumlu
 * ("Ali 2. sırada"), hem olumsuz ("Doktorun arabası kırmızı değil"),
 * hem de göreceli ("Mavi araba, doktordan önce").
 *
 * NEDEN AYRI BİR ŞABLON: `logic_grid` tek boyutlu bir sıralama görevi
 * — boydan sıraya dizme. Orada kesişim yok, tek bir zincir var.
 * Burada iki özellik birbirini kısıtlıyor ve çocuk bir özellikten
 * ötekine geçerek eleme yapmak zorunda. İkisi farklı iş.
 *
 * SIRA ÇAPA, ÖTEKİLER PERMÜTASYON. Sıra sabit tutuluyor (1..N) ve
 * isim/meslek/renk bu sıraya dağılıyor. Hepsi birden permütasyon
 * olsaydı aynı çözümün N! ayrı yazılışı olurdu ve "tek çözüm" sayımı
 * anlamını yitirirdi.
 * ================================================================== */

const ZEBRA_ISIM = ['Ali', 'Mert', 'Zeynep', 'Elif', 'Can', 'Buse']
/** Ekleri DÜZENLİ meslekler: "pilottan", "doktorun", "ressamın" */
const ZEBRA_MESLEK = ['doktor', 'öğretmen', 'pilot', 'ressam']
/** Renk hiçbir zaman tek başına bilgi taşımıyor: sayfada YAZIYLA duruyor */
const ZEBRA_RENK = ['mavi', 'kırmızı', 'yeşil', 'sarı']

type Kategori = 'isim' | 'meslek' | 'renk'

/** Yalın hâl: "Ali", "doktor", "mavi araba" */
function yalin(kat: Kategori, deger: string): string {
  return kat === 'renk' ? `${deger} araba` : deger
}
/** Ayrılma hâli: "Ali'den", "doktordan", "mavi arabadan" */
function ayrilmaHali(kat: Kategori, deger: string): string {
  if (kat === 'renk') return `${deger} ${ayrilma('araba')}`
  return ayrilma(deger, kat === 'isim')
}
/** Tamlayan hâli: "Ali'nin", "doktorun" */
function tamlayanHali(kat: Kategori, deger: string): string {
  return tamlayan(deger, kat === 'isim')
}

/** Bir atama: kategori → sıra indeksine göre değer dizisi */
type Atama = Record<Kategori, string[]>

interface Ipucu {
  metin: string
  /** Atama bu ipucuna uyuyor mu */
  gecerli(a: Atama): boolean
}

/** Bir dizinin bütün permütasyonları */
function permutasyonlar<T>(items: readonly T[]): T[][] {
  if (items.length <= 1) return [items.slice()]
  const out: T[][] = []
  for (let i = 0; i < items.length; i++) {
    const kalan = items.slice(0, i).concat(items.slice(i + 1))
    for (const alt of permutasyonlar(kalan)) out.push([items[i]!, ...alt])
  }
  return out
}

/**
 * İpuçlarına uyan atama sayısı.
 *
 * `limit` ile erken çıkıyor: "tek mi" sorusunun cevabı için ikinciyi
 * görmek yeter, hepsini saymak gerekmiyor.
 */
function cozumSayisi(
  kategoriler: Kategori[],
  degerler: Record<Kategori, string[]>,
  ipuclari: Ipucu[],
  limit = 2,
): number {
  const perms = kategoriler.map((k) => permutasyonlar(degerler[k]))
  let sayi = 0
  const gez = (i: number, kismi: Partial<Atama>): void => {
    if (sayi >= limit) return
    if (i === kategoriler.length) {
      const a = kismi as Atama
      if (ipuclari.every((ip) => ip.gecerli(a))) sayi++
      return
    }
    for (const perm of perms[i]!) {
      if (sayi >= limit) return
      gez(i + 1, { ...kismi, [kategoriler[i]!]: perm })
    }
  }
  gez(0, {})
  return sayi
}

export const zebraGrid: ExerciseTemplate = {
  code: 'zebra_grid',
  title: 'Kim hangisi?',
  domains: ['working_memory_verbal', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /**
       * Sıra sayısı en fazla 4.
       *
       * Tekillik, bütün atamalar sayılarak doğrulanıyor: N=4 ve üç
       * özellik için 24³ = 13.824 atama, hesapta sorun değil. N=5
       * 1,7 milyon yapıyor ve sayfa üretimi haftalık set kurarken
       * görünür şekilde yavaşlıyor. Zorluğu özellik sayısından ve
       * ipucu türünden almak daha ucuz.
       */
      sira: scale(l, 3, 4),
      /** Üçüncü özellik (araba rengi) 5. düzeyden sonra */
      ucOzellik: l >= 5,
      /** Göreceli ipuçları ("önce", "yan yana") 4. düzeyden sonra */
      goreceli: l >= 4,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const n = p['sira'] as number
    const ucOzellik = p['ucOzellik'] as boolean
    const goreceli = p['goreceli'] as boolean

    const kategoriler: Kategori[] = ucOzellik
      ? ['isim', 'meslek', 'renk']
      : ['isim', 'meslek']

    const degerler: Record<Kategori, string[]> = {
      isim: rng.sample(ZEBRA_ISIM, n),
      meslek: rng.sample(ZEBRA_MESLEK, n),
      renk: rng.sample(ZEBRA_RENK, n),
    }

    // Doğru çözüm: her kategori için sıraya dağılım
    const dogru: Atama = {
      isim: rng.shuffle(degerler.isim),
      meslek: rng.shuffle(degerler.meslek),
      renk: rng.shuffle(degerler.renk),
    }

    const yer = (kat: Kategori, deger: string): number => dogru[kat].indexOf(deger)

    /* ---- Aday ipuçları: HEPSİ doğru çözümden türetiliyor ---- */
    const adaylar: Ipucu[] = []

    for (const kat of kategoriler) {
      for (const d of degerler[kat]) {
        const i = yer(kat, d)
        // Olumlu konum
        adaylar.push({
          metin: `${basHarf(yalin(kat, d))} ${i + 1}. sırada.`,
          gecerli: (a) => a[kat][i] === d,
        })
        // Olumsuz konum: doğru olmayan bir sıra
        for (let j = 0; j < n; j++) {
          if (j === i) continue
          adaylar.push({
            metin: `${basHarf(yalin(kat, d))} ${j + 1}. sırada değil.`,
            gecerli: (a) => a[kat][j] !== d,
          })
        }
      }
    }

    // Kategoriler arası: aynı sırada olan / olmayan ikililer
    for (let ki = 0; ki < kategoriler.length; ki++) {
      for (let kj = ki + 1; kj < kategoriler.length; kj++) {
        const ka = kategoriler[ki]!
        const kb = kategoriler[kj]!
        for (const da of degerler[ka]) {
          const i = yer(ka, da)
          for (const db of degerler[kb]) {
            const esit = yer(kb, db) === i
            if (esit) {
              adaylar.push({
                metin:
                  kb === 'renk'
                    ? `${basHarf(tamlayanHali(ka, da))} arabası ${db}.`
                    : `${basHarf(yalin(ka, da))} ${yalin(kb, db)}.`,
                gecerli: (a) => a[ka].indexOf(da) === a[kb].indexOf(db),
              })
            } else {
              adaylar.push({
                metin:
                  kb === 'renk'
                    ? `${basHarf(tamlayanHali(ka, da))} arabası ${db} değil.`
                    : `${basHarf(yalin(ka, da))} ${yalin(kb, db)} değil.`,
                gecerli: (a) => a[ka].indexOf(da) !== a[kb].indexOf(db),
              })
            }
          }
        }
      }
    }

    if (goreceli) {
      /*
       * Göreceli ipuçları SIRAYA dayanıyor ve bu yüzden sıra çapa
       * olmak zorunda. "Önce" ve "yan yana" iki ayrı güçte bilgi:
       * birincisi aralığı daraltıyor, ikincisi komşuluk kuruyor.
       */
      const tumDegerler: { kat: Kategori; deger: string }[] = []
      for (const kat of kategoriler) for (const d of degerler[kat]) tumDegerler.push({ kat, deger: d })

      for (const a of tumDegerler) {
        for (const b of tumDegerler) {
          if (a.kat === b.kat && a.deger === b.deger) continue
          const ia = yer(a.kat, a.deger)
          const ib = yer(b.kat, b.deger)
          if (ia === ib) continue
          if (ia < ib) {
            adaylar.push({
              metin: `${basHarf(yalin(a.kat, a.deger))}, ${ayrilmaHali(b.kat, b.deger)} önce.`,
              gecerli: (x) => x[a.kat].indexOf(a.deger) < x[b.kat].indexOf(b.deger),
            })
          }
          if (Math.abs(ia - ib) === 1 && a.kat !== b.kat) {
            adaylar.push({
              metin: `${basHarf(yalin(a.kat, a.deger))} ile ${yalin(b.kat, b.deger)} yan yana.`,
              gecerli: (x) =>
                Math.abs(x[a.kat].indexOf(a.deger) - x[b.kat].indexOf(b.deger)) === 1,
            })
          }
        }
      }
    }

    /* ---- Tekillik görülene kadar ipucu EKLE ---- */
    const karisik = rng.shuffle(adaylar)
    const secili: Ipucu[] = []
    for (const ip of karisik) {
      if (cozumSayisi(kategoriler, degerler, secili) === 1) break
      secili.push(ip)
    }

    /*
     * Fazlalıkları AT.
     *
     * Ekleme sırası rastgele olduğu için seçili kümede, sonradan
     * eklenenlerin gereksiz kıldığı ipuçları kalıyor. Fazla ipucu
     * sayfayı kolaylaştırmıyor; uzatıyor ve çocuğu okumaktan
     * yoruyor. Her birini çıkarıp tekillik bozuluyor mu diye
     * bakılıyor.
     */
    for (let i = secili.length - 1; i >= 0; i--) {
      const aday = secili.filter((_, k) => k !== i)
      if (cozumSayisi(kategoriler, degerler, aday) === 1) secili.splice(i, 1)
    }

    /*
     * İpuçları KARIŞTIRILIYOR.
     *
     * Türetme sırası çözümün sırasıdır; karıştırılmazsa ipuçları
     * baştan sona okununca cevabı veriyor ve görev tümdengelim
     * olmaktan çıkıp kopyalamaya dönüyor. `logic_grid`'de tam olarak
     * bu hata vardı.
     */
    const ipuclari = rng.shuffle(secili)

    const render: RenderNode[] = [
      {
        t: 'text',
        value: ipuclari.map((ip, i) => `${i + 1}) ${ip.metin}`).join('  '),
        size: 'md',
      },
    ]

    const cozum: string[] = []
    for (const kat of kategoriler) {
      const baslik: Record<Kategori, string> = {
        isim: 'İsim',
        meslek: 'Meslek',
        renk: 'Araba rengi',
      }
      render.push({ t: 'text', value: `${baslik[kat]}:`, size: 'sm', bold: true })
      render.push({
        t: 'rows',
        items: Array.from({ length: n }, (_, i) => ({
          label: `${i + 1}. sıra`,
          blank: true,
          id: `${kat}${i}`,
        })),
        columns: 1,
        palette: degerler[kat].map((d) => ({ glyph: d })),
      })
      for (let i = 0; i < n; i++) cozum.push(dogru[kat][i]!)
    }

    return {
      code: 'zebra_grid',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        'İpuçlarını oku ve her sıraya kimin, hangi mesleğin' +
        (ucOzellik ? ' ve hangi renk arabanın' : '') +
        ' geldiğini bul. Tek bir doğru dağılım var.',
      spokenPrompt:
        'Sana birkaç ipucu vereceğim. Bir ipucu tek başına cevabı vermiyor; ' +
        'birbirleriyle birlikte düşüneceksin.',
      render,
      solution: cozum,
      grading: 'auto',
      estimatedSeconds: ipuclari.length * 20 + n * kategoriler.length * 10,
      itemCount: cozum.length,
    }
  },
  grade(instance, answer): GradeResult {
    return gradeList(instance.solution as string[], answer)
  },
  anahtar(instance) {
    const sol = instance.solution as string[]
    const n = instance.params['sira'] as number
    const parcalar: string[] = []
    for (let i = 0; i < sol.length; i += n) {
      parcalar.push(sol.slice(i, i + n).map((d, k) => `${k + 1}:${d}`).join(' '))
    }
    return parcalar.join('   —   ')
  },
}

/* ================================================================== *
 * 44. Terazi
 *
 * Birbirine bağlı dengeli teraziler, sembollerin gizli değerleri ve
 * "kaç tane gerekir" sorusu.
 *
 * DEĞERLER ZİNCİR: her sembol bir öncekinin tam katı. Rastgele
 * değerler verilip sonra "bölünüyor mu" diye bakmak, elemeyle üretim
 * demek olurdu; burada bölünebilirlik kurgunun kendisinden geliyor ve
 * cevap HER ZAMAN tam sayı. Kesirli bir cevap, çocuğun yarım üçgen
 * koymasını istemek demek.
 *
 * DÜĞÜM SAYI TAŞIMIYOR. Sembollerin değerleri yalnızca çözümde;
 * çizim ağacında olsaydı sayfanın cevabı yazdırma HTML'inin içinde
 * yazardı.
 * ================================================================== */

/** Konturlu şekiller: dolu/boş ayrımı değer taşımıyor, şekil taşıyor */
const TERAZI_SEKIL: ShapeName[] = [
  'daire_bos',
  'ucgen_bos',
  'kare_bos',
  'elmas_bos',
  'besgen_bos',
]

export const balanceScale: ExerciseTemplate = {
  code: 'balance_scale',
  title: 'Terazi',
  domains: ['working_memory_visual', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Kaç farklı sembol — zincirin uzunluğu */
      sembol: scale(l, 2, 4),
      /** Zinciri iki adım atlayan kısayol terazisi */
      kisayol: l >= 6,
      /** Cevap sembolü en küçük olan mı, yoksa zincirin ortasından mı */
      zorHedef: l >= 4,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const sembolSayisi = p['sembol'] as number
    const kisayol = p['kisayol'] as boolean
    const zorHedef = p['zorHedef'] as boolean

    const sekiller = rng.sample(TERAZI_SEKIL, sembolSayisi)
    /*
     * Zincir: v[0] = 1, v[i] = kat * v[i-1]. Kat 2 ya da 3 — 4 ve
     * üstü, dört sembollü bir kurguda en büyük değeri 64'e çıkarıp
     * soruyu "kaç tane" olmaktan çıkarıp büyük sayı saymaya
     * dönüştürüyor.
     */
    const deger: number[] = [1]
    for (let i = 1; i < sembolSayisi; i++) deger.push(deger[i - 1]! * rng.int(2, 3))

    /** Bir toplamı en büyükten başlayarak sembollere böler */
    const boluntu = (toplam: number): number[] => {
      const sayim = new Array<number>(sembolSayisi).fill(0)
      let kalan = toplam
      for (let i = sembolSayisi - 1; i >= 0; i--) {
        sayim[i] = Math.floor(kalan / deger[i]!)
        kalan -= sayim[i]! * deger[i]!
      }
      return sayim
    }

    /*
     * SORU ÖNCE SEÇİLİYOR, kefe sonra kuruluyor — ama hangi (hedef,
     * cevap) ikilisinin işe yaradığı DENENEREK değil SAYILARAK
     * bulunuyor. İlk kurguda cevap rastgele seçiliyordu ve üç ayrı
     * şey bozuluyordu: sol kefeye tek sembol düşüyordu (soru,
     * zincirdeki bir ilişkiyi okumaktan ibaret kalıyordu), zincirin
     * büyük sembolleri soruya hiç girmiyordu (süs oluyorlardı), ve
     * bazı kefelere yirmi dört şekil geliyordu (kâğıda çizilemez).
     *
     * Uygun ikililerin tamamı burada eleniyor; kurgu bulunamama diye
     * bir durum yok çünkü hiçbiri uymazsa en gevşek koşula
     * düşülüyor.
     */
    const ustSinir = sembolSayisi >= 3 && zorHedef ? sembolSayisi - 2 : 0
    const adaylar: { hedef: number; cevap: number; sayim: number[] }[] = []
    const gevsek: { hedef: number; cevap: number; sayim: number[] }[] = []
    for (let m = 0; m <= ustSinir; m++) {
      for (let c = 2; c <= 9; c++) {
        const toplam = c * deger[m]!
        const sayim = boluntu(toplam)
        const parca = sayim.reduce((a, b) => a + b, 0)
        const cesit = sayim.filter((x) => x > 0).length
        // Kefeye altıdan fazla şekil sığmıyor; tek parça da soru değil
        if (parca < 2 || parca > 6) continue
        gevsek.push({ hedef: m, cevap: c, sayim })
        // Zincirin tepesi soruya GİRSİN: girmezse büyük semboller süs
        if (sayim[sembolSayisi - 1]! > 0 && cesit >= 2) {
          adaylar.push({ hedef: m, cevap: c, sayim })
        }
      }
    }
    const secim = rng.pick(adaylar.length > 0 ? adaylar : gevsek)

    const denge: { sol: ShapeName[]; sag: ShapeName[] }[] = []
    for (let i = 1; i < sembolSayisi; i++) {
      const kat = deger[i]! / deger[i - 1]!
      denge.push({
        sol: Array.from({ length: kat }, () => sekiller[i - 1]!),
        sag: [sekiller[i]!],
      })
    }
    if (kisayol && sembolSayisi >= 3) {
      /*
       * Zinciri iki adım atlayan terazi: 1×S[j] = c×S[i], j-i ≥ 2.
       *
       * Bilgi olarak FAZLALIK — zincirden zaten çıkıyor. Kasıtlı:
       * çocuk ya iki adımı tek tek çevirecek ya da kısayolu fark
       * edecek, ve ikisi de doğru. Fazlalığı olmayan bir denklem
       * takımında "fark etme" diye bir şey olmuyor.
       *
       * Kefeye sığmıyorsa (c > 6) hiç basılmıyor: sekiz şekillik bir
       * kefe kâğıtta okunmuyor.
       */
      for (let j = sembolSayisi - 1; j >= 2; j--) {
        const c = deger[j]! / deger[j - 2]!
        if (c <= 6) {
          denge.push({
            sol: [sekiller[j]!],
            sag: Array.from({ length: c }, () => sekiller[j - 2]!),
          })
          break
        }
      }
    }

    const solKefe: ShapeName[] = []
    for (let i = sembolSayisi - 1; i >= 0; i--) {
      for (let k = 0; k < secim.sayim[i]!; k++) solKefe.push(sekiller[i]!)
    }

    return {
      code: 'balance_scale',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        'Yukarıdaki teraziler dengede. Son terazinin de dengede olması için ' +
        'sağ kefeye kaç tane gerekiyor?',
      spokenPrompt:
        'Teraziler dengede: iki kefenin ağırlığı eşit. Üstteki terazilerden ' +
        'hangi şeklin kaç tane ettiğini çıkar, sonra soruyu cevapla.',
      render: [
        {
          t: 'scales',
          items: [
            ...denge,
            { sol: solKefe, sag: [], soru: true, soruSekli: sekiller[secim.hedef]! },
          ],
        },
        {
          t: 'rows',
          items: [{ label: 'Kaç tane?', blank: true, id: 'cevap' }],
          columns: 1,
          palette: Array.from({ length: 9 }, (_, i) => ({ glyph: String(i + 1) })),
        },
      ],
      solution: {
        cevap: String(secim.cevap),
        degerler: sekiller.map((s, i) => `${s}=${deger[i]}`),
      },
      grading: 'auto',
      estimatedSeconds: 30 + denge.length * 20,
      itemCount: 1,
    }
  },
  grade(instance, answer): GradeResult {
    const sol = instance.solution as { cevap: string }
    const verilen = Array.isArray(answer) ? answer[0] : answer
    const v = verilen === undefined || verilen === null || String(verilen) === '' ? null : String(verilen)
    const dogru = v !== null && foldTr(v.trim()) === foldTr(sol.cevap)
    const items: ItemResult[] = [
      { id: 'cevap', beklenen: sol.cevap, verilen: v, dogru },
    ]
    return {
      correct: dogru ? 1 : 0,
      total: 1,
      accuracy: dogru ? 1 : 0,
      perItem: [dogru],
      items,
    }
  },
  anahtar(instance) {
    const sol = instance.solution as { cevap: string; degerler: string[] }
    return `${sol.cevap}   (${sol.degerler.join(', ')})`
  },
}
